import React, {Component} from 'react';
import PropTypes from 'prop-types';
import './style.scss';

/**
 * @class
 * @name ProfileImage
 * @extends Component
 * @param {Object} user - all the user data
 * @param {Boolean} allowEditing - whether the user can edit
 * @param {String} authToken - Fiverr authentication token
 * Returns the markup of the user profile image
 */

class ProfileImage extends Component {

    constructor(props) {
        super(props);

        this.state = {
            file: null,
            saving: false,
            newImage: ''
        };

        this.onInputChange = this.onInputChange.bind(this);
        this.displayImage = this.displayImage.bind(this);
    }

    onInputChange(e) {

        const file = e.target.files[0];

        if (!file) {
            return;
        }

        this.setState({
            file,
            saving: true
        }, () => {
            this.directUploadFile();
        });
    }

    displayImage(fileUrl) {

        this.setState({
            file: null,
            saving: false,
            newImage: fileUrl
        });

    }

    directUploadFile() {

        const startTime = Date.now(),
            file = this.state.file;

        gofor('/attachments/policy', {
            method: 'POST',
            body: JSON.stringify({'object_name': 'profile', 'attachment_name': 'photo'})
        })
            .then((response) => response.json())
            .then((data) => {

                Fiverr._broadcastLogEvents({
                    facility: 'graphite',
                    type: 'timing',
                    label: 'profile.filerr.seller_page.s3_policy',
                    value: (Date.now() - startTime)
                });

                const policy = data.policy.fiverr_policy;

                if (policy.supported_file_types.indexOf(file.type) === -1) {

                    // unsupported file
                    Fiverr._logEventToGraphite({
                        label: `profile.filerr.seller_page.ajax.unsupported_file.${file.name.split('.').pop()}`,
                        percentage: 100
                    });

                    this.setState({
                        file: null,
                        saving: false,
                        newImage: ''
                    });

                } else {
                    this.uploadFileToS3(data);
                }

            })
            .catch((err) => { throw err; });

    }

    uploadFileToS3(policyResponse) {

        const s3Policy = policyResponse.policy.s3_policy,
            clientUniqId = Math.floor(Math.random() * 26) + Date.now(),
            clientGuid = s3Policy.guid + s3Policy.client_uniq_id_prefix + clientUniqId,
            key = s3Policy.key.replace(s3Policy.guid, clientGuid),
            s3StartTime = Date.now(),
            formData = new FormData(),
            file = this.state.file;

        formData.append('acl', s3Policy.acl);
        formData.append('AWSAccessKeyId', s3Policy.AWSAccessKeyId);
        formData.append('success_action_status', s3Policy.success_action_status);
        formData.append('key', key);
        formData.append('policy', s3Policy.policy);
        formData.append('signature', s3Policy.signature);
        formData.append('content-type', file.type);
        formData.append('file', file);

        fetch(s3Policy.action_url, {
            method: 'POST',
            body: formData
        })
            .then((response) => response.text())
            .then((data) => {

                Fiverr._broadcastLogEvents({
                    facility: 'graphite',
                    type: 'timing',
                    label: 'profile.filerr.seller_page.s3_direct_upload',
                    value: (Date.now() - s3StartTime)
                });

                // the response is html, so needs to be converted to a fragment before
                // we can do anything with it
                const frag = document.createRange().createContextualFragment(data),
                    fragKey = frag.querySelector('Key').firstChild.nodeValue,
                    fragBucket = frag.querySelector('Bucket').firstChild.nodeValue;

                // after the file has uploaded successfully, we will create a pending attachment and
                // link it to the user profile

                const formDataTemp = new FormData();
                formDataTemp.append('authenticity_token', this.props.authToken);
                formDataTemp.append('s3_file_path', fragKey);
                formDataTemp.append('bucket_name', fragBucket);

                return fetch('/profiles/update_photo', {
                    method: 'POST',
                    body: formDataTemp,
                    headers: new Headers({
                        'Accept': 'application/json',
                        'X-CSRF-Token': this.props.authToken
                    }),
                    credentials: 'same-origin'
                });

            })
            .then((response) => response.json())
            .then((data) => {

                Fiverr._broadcastLogEvents({
                    facility: 'graphite',
                    type: 'timing',
                    label: 'profile.filerr.seller_page.update_photo',
                    value: (Date.now() - s3StartTime)
                });

                this.displayImage(data.url);

            })
            .catch((err) => {

                Fiverr._logEventToGraphite({
                    label: 'profile.filerr.seller_page.ajax.s3_upload.error',
                    percentage: 100
                });

                Fiverr._logErrorToServer('seller_card_package', 'Seller Filerr photo', `[s3_direct_upload] ${err.responseText}, status:${err.status}`);
            });

    }

    render() {

        const {
            user,
            allowEditing
        } = this.props;

        const showUserBadge = user.is_seller && user.achievement && (user.achievement > 0),
            badgeClasses = [
                { rating: 0, class: 'no_level_seller' },
                { rating: 1, class: 'level_one_seller' },
                { rating: 2, class: 'level_two_seller' },
                { rating: 5, class: 'top_rated_seller' }
            ],
            badgeClass = showUserBadge ? badgeClasses.find((b) => b.rating === user.achievement).class : '',
            newImage = this.state.newImage,
            hasImage = user.has_profile_photo || newImage,
            userNameLetter = user.username ? user.username.substring(0, 1) : 'x';

        let userImage,
            labelClass = '';

        if (allowEditing && !this.state.saving) {
            labelClass = 'editable';
        } else if (this.state.saving) {
            labelClass = 'loading';
        }

        if (hasImage) {
            userImage = <img src={newImage ? newImage : user.profile_photo} className="user-pict-img" alt={user.username} width="150" height="150"/>;
        } else {
            userImage = <span className="missing-image-user">{userNameLetter}</span>;
        }

        return (
            <label className={`user-pict-150 ${labelClass}`} htmlFor="profile_image">

                {allowEditing &&
                    <input accept="image/png,image/jpeg" className="hidden" onChange={this.onInputChange} id="profile_image" name="profile[image]" type="file"/>
                }

                {userImage}

                {showUserBadge &&
                    <a href="/levels" className={`user-badge-round-med ${badgeClass}`}></a>
                }

            </label>
        );

    }

}

ProfileImage.propTypes = {
    user: PropTypes.object.isRequired,
    allowEditing: PropTypes.bool.isRequired,
    authToken: PropTypes.string.isRequired
};

export default ProfileImage;


// WEBPACK FOOTER //
// ./src/components/profile_image/index.js